import { useEffect, useRef, useState } from "react";

import {
  faEmptySet,
  faFilter,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Column, Table } from "@tanstack/react-table";
import { AnimatePresence } from "motion/react";

import { Input } from "src/base-components/Input";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import {
  ALWAYS_VISIBLE_COLUMNS,
  AUX_DATA_COLUMN_PREFIX,
  EXPECTED_DATA_COLUMN_PREFIX,
  INTERMEDIATE_TABLE_PREFIXES,
} from "src/dataTable/TablePrefixes";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import { Button } from "src/design-system/Button";
import { DropdownItem } from "src/design-system/Dropdown";
import { EmptyState } from "src/design-system/EmptyState";
import { ResultAllColumnsSelectorRow } from "src/nodeEditor/ResultAllColumnsSelectorRow";
import { ResultColumnSelectorRow } from "src/nodeEditor/ResultColumnSelectorRow";
import { useFuseSearch } from "src/utils/useFuseSearch";

const isAlwaysVisibleColumn = (column: Column<ResultDataAndAuxRowV2>) =>
  (ALWAYS_VISIBLE_COLUMNS as readonly string[]).includes(column.id);

type SearchableSelectProps = {
  tableInstance: Table<ResultDataAndAuxRowV2>;
};

const COLUMNS_SUBHEADERS: Record<string, string> = {
  [AUX_DATA_COLUMN_PREFIX]: "Additional Fields",
  [EXPECTED_DATA_COLUMN_PREFIX]: "Expected Output Fields",
} as const;

export const IntermediateTableColumnPicker = ({
  tableInstance,
}: SearchableSelectProps) => {
  const options = tableInstance
    .getAllColumns()
    .filter((col) => !isAlwaysVisibleColumn(col))
    .map((column) => {
      const type = INTERMEDIATE_TABLE_PREFIXES.find((prefix) =>
        column.id.startsWith(prefix),
      );
      return {
        type,
        name: column.id.slice(type?.length),
        column,
      };
    })
    .sort(
      (a, b) =>
        INTERMEDIATE_TABLE_PREFIXES.indexOf(a.type!) -
        INTERMEDIATE_TABLE_PREFIXES.indexOf(b.type!),
    );

  const [query, setQuery] = useState("");

  const search = useFuseSearch(options, {
    shouldSort: false,
    keys: [`name`],
    threshold: 0,
    ignoreLocation: true,
  });

  const results = search(query);
  const columnIds = results.map((item) => item.column.id);

  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            as={Button}
            dataLoc="column-picker-button"
            iconLeft={faFilter}
            variant="secondary"
            fullWidth
          >
            View options
          </PopoverButton>
          <AnimatePresence initial={false} onExitComplete={() => setQuery("")}>
            {open && (
              <PopoverPanel
                anchor={{
                  to: "bottom end",
                  gap: 8,
                }}
                as={MotionDropdownPanel}
                className="w-70"
                data-loc="column-picker-popover-options"
                static
                unmount
              >
                <div className="px-4 py-2">
                  <AutoFocusInput value={query} onChange={setQuery} />
                </div>
                <div className="decideScrollbar max-h-60 overflow-y-auto">
                  {results.length > 0 ? (
                    <ul>
                      <ResultAllColumnsSelectorRow
                        resultsColumns={columnIds}
                        toggleColumnsVisibility={
                          tableInstance.setColumnVisibility
                        }
                        visibleColumnsCount={
                          tableInstance
                            .getVisibleFlatColumns()
                            .filter((col) => columnIds.includes(col.id)).length
                        }
                      />
                      {results.flatMap((item, index, array) => {
                        const items = [];

                        const previousItem = array[index - 1];
                        if (
                          item.type &&
                          COLUMNS_SUBHEADERS[item.type] &&
                          (!previousItem || item.type !== previousItem.type)
                        ) {
                          items.push(
                            <div
                              key={`heading${item.type}`}
                              className="flex h-12 items-center border-gray-100 border-t px-4 py-2.5 font-inter-medium-12px text-gray-800"
                            >
                              {COLUMNS_SUBHEADERS[item.type]}
                            </div>,
                          );
                        }

                        items.push(
                          <DropdownItem
                            key={item.column.id}
                            // Make padding 0 to make the entire row width clickable
                            className="px-0"
                          >
                            <ResultColumnSelectorRow
                              column={item.column}
                              query={query}
                            />
                          </DropdownItem>,
                        );

                        return items;
                      })}
                    </ul>
                  ) : (
                    <EmptyState
                      description="Change your search query to try again"
                      headline="No items found"
                      icon={faEmptySet}
                    />
                  )}
                </div>
              </PopoverPanel>
            )}
          </AnimatePresence>
        </>
      )}
    </Popover>
  );
};

const AutoFocusInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [hasFocused, setHasFocused] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (ref.current && !hasFocused) {
      const timeout = setTimeout(() => {
        ref.current?.focus();
        setHasFocused(true);
      }, 10);

      return () => clearTimeout(timeout);
    }
  }, [hasFocused]);

  return (
    <Input
      ref={ref}
      data-loc="column-picker-search"
      placeholder="Search for a field"
      prefixIcon={{ icon: faSearch }}
      value={value}
      autoFocus
      fullWidth
      onChange={(e) => onChange?.(e.target.value)}
    />
  );
};
